// src/pages/Characters.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAuthenticated, getMembershipType, getDestinyMembershipId } from '../services/authService';
import { getUserCharacters, Character } from '../services/characterService';
import LoadingState from '../components/common/LoadingState';
import { debugAuthState } from '../services/authDebugUtils';
import DebugPanel from '../components/debug/DebugPanel';

const Characters: React.FC = () => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCharacters = async () => {
      // Get debug info
      const authDebugInfo = debugAuthState();
      setDebugInfo(authDebugInfo);
      
      const loggedIn = isAuthenticated();
      setAuthenticated(loggedIn);

      if (!loggedIn) {
        setLoading(false);
        return;
      }

      try {
        // Log membership info for debugging
        console.log('Membership info:', {
          type: getMembershipType(),
          id: getDestinyMembershipId(),
          authState: authDebugInfo
        });
        
        setLoading(true);
        const data = await getUserCharacters();
        setCharacters(data);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching characters:', err);
        setError(err.message || 'Failed to load characters');
      } finally {
        setLoading(false);
      }
    };

    fetchCharacters();
    
    // Listen for auth state changes
    const handleAuthChange = () => {
      console.log("Auth state change detected in Characters component");
      fetchCharacters();
    };
    
    window.addEventListener('auth-state-changed', handleAuthChange);
    
    return () => {
      window.removeEventListener('auth-state-changed', handleAuthChange);
    };
  }, []);

  const formatPlayTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else {
      return `${hours}h ${minutes % 60}m`;
    }
  };

  const handleLoginRedirect = () => {
    // Store the current path to return after login
    sessionStorage.setItem('returnPath', '/characters');
    navigate('/login');
  };

  if (loading) {
    return <LoadingState message="Loading characters..." />;
  }

  if (!authenticated) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-destiny-gold mb-4">Authentication Required</h2>
        <p className="text-gray-300 mb-4">
          Please sign in with your Bungie account to view your characters.
        </p>
        <button
          onClick={handleLoginRedirect}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-destiny-blue hover:bg-opacity-90"
        >
          Sign in with Bungie
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-xl font-bold text-destiny-red mb-4">Error Loading Characters</h2>
        <p className="text-gray-300 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-destiny-blue hover:bg-opacity-90"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-destiny-gold mb-6">Your Characters</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {characters.length > 0 ? (
          characters.map((char) => (
            <div key={char.characterId} className="bg-gray-800 rounded-lg overflow-hidden shadow-lg">
              {/* Character banner/emblem */}
              <div 
                className="h-24 bg-gray-700 relative"
                style={{
                  backgroundImage: char.emblemBackgroundPath ? `url(${char.emblemBackgroundPath})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="absolute inset-0 flex items-center p-4">
                  {char.emblemPath && (
                    <img
                      src={char.emblemPath}
                      alt={`${char.className} emblem`}
                      className="h-14 w-14 mr-3 rounded"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = '/placeholder-emblem.png';
                      }}
                    />
                  )}
                  <div>
                    <h2 className="text-white text-lg font-bold drop-shadow-lg">{char.className}</h2>
                    <p className="text-gray-200 text-sm drop-shadow-lg">{char.race} {char.gender}</p>
                  </div>
                  {/* Light level badge */}
                  <div className="absolute bottom-2 right-2 bg-destiny-dark bg-opacity-80 px-2 py-1 rounded">
                    <span className="text-destiny-gold font-bold">✧ {char.light}</span>
                  </div>
                </div>
              </div>

              {/* Character details */}
              <div className="p-4">
                <div className="mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Last played:</span>
                    <span className="text-white">
                      {new Date(char.lastPlayed).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Play time:</span>
                    <span className="text-white">{formatPlayTime(char.minutesPlayedTotal)}</span>
                  </div>
                </div>

                {/* Character actions */}
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Link 
                    to={`/characters/${char.characterId}`}
                    className="bg-destiny-blue hover:bg-opacity-90 text-white py-2 px-4 rounded text-sm text-center"
                  >
                    View Details
                  </Link>
                  <Link 
                    to={`/achievements?character=${char.characterId}`}
                    className="bg-gray-700 hover:bg-gray-600 text-white py-2 px-4 rounded text-sm text-center"
                  >
                    Achievements
                  </Link>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-10 bg-gray-800 rounded-lg">
            <p className="text-gray-400">No characters found for your Destiny 2 account.</p>
            <p className="text-gray-500 mt-2 text-sm">
              If you have characters in Destiny 2, please try refreshing the page.
            </p>
          </div>
        )}
      </div>
      
      {/* Debug Panel */}
      <DebugPanel initialDebugInfo={debugInfo} />
    </div>
  );
};

export default Characters;